import { useState } from 'react'
import { Outlet } from '@tanstack/react-router'
import { Menu } from 'lucide-react'
import { useTranslation } from '@kostbar/i18n'
import { Sidebar } from './sidebar'
import { Header } from './header'
import { LocationSwitcher } from './location-switcher'
import { Button } from '@/components/ui/button'
import { Sheet, SheetContent, SheetTrigger } from '@/components/ui/sheet'

export function AppShell() {
  const { t: tApp } = useTranslation('app')
  const [sidebarOpen, setSidebarOpen] = useState(false)

  // The data attributes below are print hooks: the shell is a fixed-height scrolling layout, which
  // would otherwise clip a printed report to the first page (see @media print in styles.css).
  return (
    <div className="h-screen flex" data-app-shell>
      {/* Desktop Sidebar - hidden on mobile */}
      <aside className="hidden lg:block w-64 flex-shrink-0" data-app-chrome>
        <Sidebar />
      </aside>

      {/* Main content area */}
      <div className="flex-1 flex flex-col overflow-hidden">
        {/* Mobile Sidebar - Sheet (drawer) */}
        <Sheet open={sidebarOpen} onOpenChange={setSidebarOpen}>
          {/* Mobile header with hamburger menu */}
          <div
            className="lg:hidden border-b border-border p-4 flex items-center gap-3"
            data-app-chrome
          >
            <SheetTrigger asChild>
              <Button variant="ghost" size="icon" aria-label="Open menu">
                <Menu className="h-5 w-5" />
              </Button>
            </SheetTrigger>
            {/* Both the title and the control give way rather than the row overflowing: at 375px
                the menu button, the title and the location together want more than the width, and
                a `shrink-0` on each pushed the control off the right edge. */}
            <h1 className="max-w-[45%] shrink-0 truncate text-lg font-bold">
              {tApp('title')}
            </h1>
            {/* Same one control as the desktop header, so a phone does not lose the selection.
                It takes the width the title leaves rather than a fixed share of the row: at 375px
                a fixed share pushed it past the right edge, and a title clipped to "Kostbar …" to
                make room for a mostly-empty control is the wrong trade. */}
            <LocationSwitcher className="ml-auto min-w-0 shrink" />
          </div>

          <SheetContent side="left" className="p-0 w-64">
            <Sidebar onNavigate={() => setSidebarOpen(false)} />
          </SheetContent>
        </Sheet>

        {/* Desktop header - hidden on mobile */}
        <div className="hidden lg:block" data-app-chrome>
          <Header />
        </div>

        <main className="flex-1 overflow-auto p-4 lg:p-6" data-app-main>
          <Outlet />
        </main>
      </div>
    </div>
  )
}
